import {ICellRendererParams} from "@ag-grid-community/core";
import {Intent, Tag, Text, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import React, {CSSProperties, useContext} from "react";
import {ManifestStop} from "../../../generated/graphql";
import {extractSimplePref, PreferenceContext} from "../../../providers/PreferenceProvider";
import {navigateToCmsOrder} from "../../../services/CmsService";
import {JobnumberTooltip, JobNumberTooltipUsage} from "./JobNumberCellRenderer";
import {AuthContext} from "../../../providers/AuthProvider";
import {useOktaAuth} from "@okta/okta-react";
import DispatchNotes from "../dispatch-notes/DispatchNotes";
import {TenantPreferences} from "../../../constants/constants";
import {AgStop, TManifestStopEnhanced} from "../types";

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const ManifestJobBadge = styled(Tag)`
  border-radius: 4px;
  background-color: transparent;
`;

const LinkText = styled(Text)<{color?: string; fontSize?: number}>`
  opacity: 1;
  font-family: Roboto;
  font-weight: 500;
  font-size: ${(props) => props.fontSize ?? "12"}px;
  color: ${(props) => props.color ?? "#1f2937"};
`;

export interface ManifestJobCellRendererParams extends ICellRendererParams<TManifestStopEnhanced, string> {
  jobColoringIndex: Map<string, number>;
  minimal?: boolean;
  fontSize?: number;
  color?: string;
  usage?: JobNumberTooltipUsage;
  dispatchNotesEnabled?: boolean;
  stops?: (ManifestStop | AgStop)[];
}

export const ManifestJobColorPalette: CSSProperties[] = [
  {backgroundColor: "#C996FD"},
  {backgroundColor: "#62EAA9"},
  {backgroundColor: "#E4D03D"},
  {backgroundColor: "#FF9495"},
  {backgroundColor: "rgba(119,179,211,0.5)"},
  {backgroundColor: "#FFAC75"},
  {backgroundColor: "rgba(235,57,235,0.5)"},
  {backgroundColor: "#7CC254"},
  {backgroundColor: "rgba(255,207,224,0.5)"},
  {backgroundColor: "#FFC328"},
  {backgroundColor: "rgba(239,64,66,0.5)"},
  {backgroundColor: "rgba(113,255,216,0.5)"},
  {backgroundColor: "rgba(228,208,61,0.5)"},
  {backgroundColor: "rgba(64,66,179,0.5)"},
  {backgroundColor: "#2AB3FD"},
  {backgroundColor: "rgba(255,172,117,0.5)"},
  {backgroundColor: "#EB39EB"},
  {backgroundColor: "rgba(124,194,84,0.5)"},
  {backgroundColor: "#42A673"},
  {backgroundColor: "#8CFF64"}
];

const MinimalStyles: CSSProperties = {
  padding: "2px 0"
};

export const ColoredJobNumberCellRenderer = (props: ManifestJobCellRendererParams) => {
  const {fontSize, color, minimal, data, jobColoringIndex, node, value, api, usage, stops} = props;
  const orderId = data?.order?.orderId;
  const authState = useContext(AuthContext);
  const {tenantPreferences} = useContext(PreferenceContext);
  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;
  const firstStopTimezone = stops?.find((stop) => stop.sequence === 1 && stop.order?.orderId === orderId)?.timeZone;

  const {oktaAuth} = useOktaAuth();

  const backgroundColorIndex = jobColoringIndex?.get(data!.job.jobNumber);
  let styling: CSSProperties = {
    backgroundColor: "transparent",
    display: "flex",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  if (backgroundColorIndex !== undefined && backgroundColorIndex < ManifestJobColorPalette.length) {
    styling = {...styling, ...ManifestJobColorPalette[backgroundColorIndex]};
  }

  if (minimal) {
    styling = {...styling, ...MinimalStyles};
  }

  return (
    <Container>
      <div style={{flexShrink: 1, overflow: "hidden"}}>
        <Tooltip
          content={<JobnumberTooltip data={props} usage={usage} />}
          popoverClassName={"job-number-tooltip-popover"}
          position="auto"
          interactionKind="hover"
          targetProps={{style: {width: "100%"}}}
        >
          <ManifestJobBadge
            style={styling}
            data-testid={`manifest-job-stop-number-button-${node.id}`}
            intent={Intent.PRIMARY}
            interactive={true}
            minimal={true}
            onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
              e.stopPropagation();
              if (!authState.tenant || !orderId) {
                return;
              }
              api.showLoadingOverlay();
              navigateToCmsOrder(authState.tenant, oktaAuth.getAccessToken() ?? "", orderId, tenantPreferences).finally(
                () => api.hideOverlay()
              );
            }}
          >
            <LinkText fontSize={fontSize} color={color} ellipsize>
              {value}
            </LinkText>
          </ManifestJobBadge>
        </Tooltip>
      </div>
      {props.dispatchNotesEnabled && (
        <div style={{flexShrink: 0, marginLeft: "4px", marginTop: "-2px"}}>
          <DispatchNotes
            identifier={`jobstop-${data?.jobStopId ?? 0}`}
            orderId={orderId ?? 0}
            orderNumber={value}
            orderEvents={data?.order?.orderEvents || []}
            timeZoneOfJobStop={timezoneOfJobStop}
            firstStopTimezone={firstStopTimezone}
            currentStopTimeZone={data?.timeZone}
          />
        </div>
      )}
    </Container>
  );
};
